import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";

/**
 * Visitor Tracking Hook
 * Tracks: session duration, page views, scroll depth, form interactions, CTA clicks, exit pages.
 * Sends data to /api/track endpoint via beacon API for reliability.
 *
 * PERF: Non-critical event listeners (scroll, form, click) are deferred via
 * requestIdleCallback to avoid adding to TBT during initial page load.
 * Only the pageview event fires immediately on route change.
 */

const SESSION_KEY = "smiths_session_id";
const SESSION_START_KEY = "smiths_session_start";
const SESSION_PAGES_KEY = "smiths_session_pages";
const DEFER_MS = 3000; // Minimum ms before attaching non-critical listeners

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getOrCreateSession(): { sessionId: string; isNew: boolean } {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) {
    return { sessionId: existing, isNew: false };
  }
  const sessionId = generateSessionId();
  sessionStorage.setItem(SESSION_KEY, sessionId);
  sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
  sessionStorage.setItem(SESSION_PAGES_KEY, "0");
  return { sessionId, isNew: true };
}

function getSessionDuration(): number {
  const start = sessionStorage.getItem(SESSION_START_KEY);
  if (!start) return 0;
  return Math.round((Date.now() - parseInt(start)) / 1000);
}

function incrementPageCount(): number {
  const current = parseInt(sessionStorage.getItem(SESSION_PAGES_KEY) || "0");
  const next = current + 1;
  sessionStorage.setItem(SESSION_PAGES_KEY, next.toString());
  return next;
}

function getDevice(): "desktop" | "tablet" | "mobile" {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  return "Other";
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
  };
}

function sendTrackEvent(data: Record<string, unknown>) {
  try {
    const payload = JSON.stringify(data);
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/track", new Blob([payload], { type: "application/json" }));
    } else {
      fetch("/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    }
  } catch {
    // Silent fail — tracking should never break the site
  }
}

/**
 * Call this after a successful form submission to track the form_submit event.
 * Can be called from any component — does not require the hook.
 */
export function trackFormSubmit(formName: string, pagePath?: string) {
  const sessionId = sessionStorage.getItem(SESSION_KEY) || "";
  if (!sessionId) return;
  sendTrackEvent({
    type: "form_submit",
    sessionId,
    pagePath: pagePath || window.location.pathname,
    metadata: JSON.stringify({ formName }),
    clientTimestamp: Date.now(),
  });
  // Meta Pixel: track Lead event on form submissions
  if (typeof window !== "undefined" && (window as any).fbq) {
    (window as any).fbq("track", "Lead", { content_name: formName });
  }
  // ChatGPT Ads Pixel: track registration_completed on form submissions
  if (typeof window !== "undefined" && (window as any).oaiq) {
    (window as any).oaiq("measure", "registration_completed", {
      type: "customer_action",
      amount: 0,
      currency: "USD",
    });
  }
}

/** Helper: schedule work after page is interactive */
function deferWork(fn: () => void) {
  const elapsed = performance.now();
  const remaining = Math.max(0, DEFER_MS - elapsed);
  const run = () => {
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(fn, { timeout: 2000 });
    } else {
      fn();
    }
  };
  if (remaining <= 0) {
    run();
  } else {
    setTimeout(run, remaining);
  }
}

export function useVisitorTracking() {
  const [location] = useLocation();
  const scrollDepthRef = useRef(0);
  const pageStartRef = useRef(Date.now());
  const lastPageRef = useRef(location);
  const formTrackingRef = useRef<Map<string, string>>(new Map());
  const deferredRef = useRef(false);

  // Track page view on route change — this is the only immediate event
  useEffect(() => {
    const { sessionId, isNew } = getOrCreateSession();
    const pageCount = incrementPageCount();
    const utm = getUTMParams();

    // Send page view event
    sendTrackEvent({
      type: "pageview",
      sessionId,
      pagePath: location,
      clientTimestamp: Date.now(),
      ...(isNew && pageCount === 1
        ? {
            newSession: true,
            landingPage: location,
            device: getDevice(),
            browser: getBrowser(),
            referrer: document.referrer || undefined,
            ...utm,
          }
        : {}),
    });

    // Send time-on-page for previous page
    if (lastPageRef.current !== location && pageCount > 1) {
      const timeOnPage = Math.round((Date.now() - pageStartRef.current) / 1000);
      sendTrackEvent({
        type: "exit_page",
        sessionId,
        pagePath: lastPageRef.current,
        timeOnPage,
        scrollDepth: scrollDepthRef.current,
        clientTimestamp: Date.now(),
      });
    }

    // Meta Pixel: track PageView on SPA route changes & ViewContent on service pages
    if ((window as any).fbq) {
      (window as any).fbq("track", "PageView");
      const servicePages = ["/services", "/wardrobes", "/kitchens", "/living-storage", "/pricing", "/ai-visualiser"];
      if (servicePages.some(p => location.startsWith(p))) {
        (window as any).fbq("track", "ViewContent", { content_name: location });
      }
    }

    // Reset for new page
    scrollDepthRef.current = 0;
    pageStartRef.current = Date.now();
    lastPageRef.current = location;
  }, [location]);

  // Deferred: scroll depth, form interactions, CTA clicks, session end
  // These are non-critical and should not block initial render / TBT
  useEffect(() => {
    if (deferredRef.current) return;
    deferredRef.current = true;

    const cleanups: (() => void)[] = [];

    deferWork(() => {
      // --- Scroll depth tracking ---
      let ticking = false;
      const handleScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (docHeight > 0) {
            const depth = Math.min(100, Math.round((scrollTop / docHeight) * 100));
            if (depth > scrollDepthRef.current) {
              scrollDepthRef.current = depth;
            }
            const milestones = [25, 50, 75, 100];
            for (const m of milestones) {
              const key = `scroll_${m}_${lastPageRef.current}`;
              if (depth >= m && !formTrackingRef.current.has(key)) {
                formTrackingRef.current.set(key, "sent");
                sendTrackEvent({
                  type: "scroll",
                  sessionId: sessionStorage.getItem(SESSION_KEY) || "",
                  pagePath: lastPageRef.current,
                  scrollDepth: m,
                  clientTimestamp: Date.now(),
                });
              }
            }
          }
          ticking = false;
        });
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", handleScroll));

      // --- Form interaction tracking ---
      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
          const form = target.closest("form");
          const formName = form?.getAttribute("data-form-name") ||
            form?.id ||
            target.closest("[data-form-name]")?.getAttribute("data-form-name") ||
            "unknown-form";
          const sessionId = sessionStorage.getItem(SESSION_KEY) || "";
          const focusKey = `form_focus_${formName}_${lastPageRef.current}`;
          if (!formTrackingRef.current.has(focusKey)) {
            formTrackingRef.current.set(focusKey, "focused");
            sendTrackEvent({
              type: "form_focus",
              sessionId,
              pagePath: lastPageRef.current,
              metadata: JSON.stringify({ formName }),
              clientTimestamp: Date.now(),
            });
          }
        }
      };
      const handleInput = (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
          const form = target.closest("form");
          const formName = form?.getAttribute("data-form-name") ||
            form?.id ||
            target.closest("[data-form-name]")?.getAttribute("data-form-name") ||
            "unknown-form";
          const sessionId = sessionStorage.getItem(SESSION_KEY) || "";
          const startKey = `form_start_${formName}_${lastPageRef.current}`;
          if (!formTrackingRef.current.has(startKey)) {
            formTrackingRef.current.set(startKey, "started");
            sendTrackEvent({
              type: "form_start",
              sessionId,
              pagePath: lastPageRef.current,
              metadata: JSON.stringify({ formName }),
              clientTimestamp: Date.now(),
            });
          }
        }
      };
      document.addEventListener("focusin", handleFocus, { passive: true });
      document.addEventListener("input", handleInput, { passive: true });
      cleanups.push(() => {
        document.removeEventListener("focusin", handleFocus);
        document.removeEventListener("input", handleInput);
      });

      // --- CTA click tracking ---
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest("a, button");
        if (!link) return;
        const text = link.textContent?.trim().slice(0, 100) || "";
        const href = link.getAttribute("href") || "";
        const isCtaLike =
          text.toLowerCase().includes("book") ||
          text.toLowerCase().includes("brochure") ||
          text.toLowerCase().includes("call") ||
          text.toLowerCase().includes("whatsapp") ||
          text.toLowerCase().includes("contact") ||
          text.toLowerCase().includes("free") ||
          text.toLowerCase().includes("quote") ||
          text.toLowerCase().includes("visuali") ||
          href.startsWith("tel:") ||
          href.includes("wa.me");
        if (isCtaLike) {
          sendTrackEvent({
            type: "cta_click",
            sessionId: sessionStorage.getItem(SESSION_KEY) || "",
            pagePath: lastPageRef.current,
            metadata: JSON.stringify({ text, href }),
            clientTimestamp: Date.now(),
          });
          // Meta Pixel: track Contact event on phone clicks
          if (href.startsWith("tel:") && (window as any).fbq) {
            (window as any).fbq("track", "Contact");
          }
        }
      };
      document.addEventListener("click", handleClick, { passive: true });
      cleanups.push(() => document.removeEventListener("click", handleClick));

      // --- Session end tracking ---
      const handleUnload = () => {
        const sessionId = sessionStorage.getItem(SESSION_KEY);
        if (!sessionId) return;
        const timeOnPage = Math.round((Date.now() - pageStartRef.current) / 1000);
        sendTrackEvent({
          type: "session_end",
          sessionId,
          pagePath: lastPageRef.current,
          duration: getSessionDuration(),
          scrollDepth: scrollDepthRef.current,
          timeOnPage,
          pageCount: parseInt(sessionStorage.getItem(SESSION_PAGES_KEY) || "1"),
          clientTimestamp: Date.now(),
        });
      };
      const handleVisibility = () => {
        if (document.visibilityState === "hidden") handleUnload();
      };
      window.addEventListener("beforeunload", handleUnload);
      document.addEventListener("visibilitychange", handleVisibility);
      cleanups.push(() => {
        window.removeEventListener("beforeunload", handleUnload);
        document.removeEventListener("visibilitychange", handleVisibility);
      });
    });

    return () => {
      cleanups.forEach(fn => fn());
    };
  }, []); // Only run once on mount
}
